import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { Chip } from '../ui/Chip';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { fmtEur } from '../../lib/utils';

interface StripePrice {
  id: string;
  productId: string;
  productName: string;
  amount: number;
  currency: string;
  interval?: 'day' | 'week' | 'month' | 'year';
  intervalCount?: number;
}

interface ClientLite { id: string; legalName: string; }

interface AttachSubscriptionModalProps {
  onClose: () => void;
  clientId?: string;
  clientName?: string;
}

export function AttachSubscriptionModal({ onClose, clientId: presetClientId, clientName }: AttachSubscriptionModalProps) {
  const qc = useQueryClient();
  const [step, setStep] = useState<'choose' | 'link'>('choose');
  const [clientId, setClientId] = useState(presetClientId ?? '');
  const [priceId, setPriceId] = useState('');
  const [searchProduct, setSearchProduct] = useState('');
  const [link, setLink] = useState<string | null>(null);

  const { data: prices, isLoading: pricesLoading, isError: pricesError } = useQuery({
    queryKey: ['stripe', 'prices'],
    queryFn: () => api.get<{ items: StripePrice[] }>('/services/stripe/prices'),
    retry: false,
  });

  const { data: clients } = useQuery({
    queryKey: ['clients-lite'],
    queryFn: () => api.get<{ items: ClientLite[] }>('/clients?pageSize=200'),
    enabled: !presetClientId,
  });

  const grouped = useMemo(() => {
    const items = prices?.items ?? [];
    const filtered = searchProduct
      ? items.filter((p) => p.productName.toLowerCase().includes(searchProduct.toLowerCase()))
      : items;
    const byProduct = new Map<string, StripePrice[]>();
    for (const p of filtered) {
      if (!byProduct.has(p.productName)) byProduct.set(p.productName, []);
      byProduct.get(p.productName)!.push(p);
    }
    return Array.from(byProduct.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [prices, searchProduct]);

  const createSession = useMutation({
    mutationFn: () => api.post<{ url: string; sessionId: string }>('/services/stripe/checkout-session', { clientId, priceId }),
    onSuccess: (r) => {
      setLink(r.url);
      setStep('link');
      qc.invalidateQueries({ queryKey: ['clients'] });
      qc.invalidateQueries({ queryKey: ['client'] });
      toast.ok('Link Checkout generato');
    },
    onError: (err) => toast.alert('Errore generazione link', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const copy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link).then(() => toast.ok('Link copiato'));
  };

  return (
    <Modal
      title="Attacca abbonamento Stripe"
      kicker={clientName ? `Cliente · ${clientName}` : 'Cliente · Abbonamento'}
      onClose={onClose}
      size="lg"
      footer={
        step === 'choose' ? (
          <>
            <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
            <div className="spacer" />
            <button
              className="btn btn-primary"
              disabled={!clientId || !priceId || createSession.isPending}
              onClick={() => createSession.mutate()}
            >
              {createSession.isPending ? 'Genero…' : <>Genera Checkout link <Icon name="arrowRight" size={12} /></>}
            </button>
          </>
        ) : (
          <>
            <button className="btn btn-ghost" onClick={() => { setStep('choose'); setLink(null); setPriceId(''); }}><Icon name="arrowLeft" size={12} /> Nuovo</button>
            <div className="spacer" />
            <button className="btn" onClick={() => link && window.open(link, '_blank')}>
              <Icon name="link" /> Apri Checkout
            </button>
            <button className="btn btn-primary" onClick={copy}>
              <Icon name="check" /> Copia link
            </button>
          </>
        )
      }
    >
      {step === 'choose' && (
        <>
          {!presetClientId && (
            <div className="form-section">
              <div className="form-section-hd">Cliente</div>
              <Field label="Cliente destinatario" required>
                <select className="inp" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                  <option value="">— seleziona —</option>
                  {(clients?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.legalName}</option>)}
                </select>
              </Field>
            </div>
          )}

          <div className="form-section">
            <div className="form-section-hd">Piano Stripe</div>
            <div className="form-grid">
              <Field span={12}>
                <div className="tb-search" style={{ margin: 0 }}>
                  <Icon name="search" size={12} />
                  <input
                    placeholder="Cerca un prodotto Stripe (es. Hosting Start, Piano Base CRM)…"
                    value={searchProduct}
                    onChange={(e) => setSearchProduct(e.target.value)}
                  />
                </div>
              </Field>
            </div>

            {pricesError && (
              <div className="mt-3 p-3 rounded-2 bg-alert-bg text-alert text-[13px] border border-alert/30">
                Errore caricamento listino Stripe. Verifica che STRIPE_SECRET_KEY sia configurato.
              </div>
            )}
            {pricesLoading && (
              <div className="mt-3 space-y-2">
                <span className="skeleton sk-block" />
                <span className="skeleton sk-block" />
              </div>
            )}

            {grouped.length > 0 && (
              <div className="mt-3" style={{ maxHeight: 380, overflowY: 'auto' }}>
                {grouped.map(([productName, items]) => (
                  <div key={productName} className="mb-3">
                    <div className="t-micro mb-2">{productName}</div>
                    <div className="flex flex-wrap gap-2">
                      {items.map((p) => {
                        const cycle = p.interval === 'year' ? 'anno' : p.interval === 'month' && p.intervalCount === 3 ? 'trim.' : 'mese';
                        const active = priceId === p.id;
                        return (
                          <button
                            key={p.id}
                            type="button"
                            className={`chip ${active ? 'active' : 'ghost'} cursor-pointer`}
                            style={{ padding: '8px 14px', fontSize: 13 }}
                            onClick={() => setPriceId(p.id)}
                          >
                            <span className="font-semibold">€ {fmtEur(p.amount)}</span>
                            <span className="mute"> / {cycle}</span>
                            {active && <Icon name="check" size={12} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!pricesLoading && !pricesError && grouped.length === 0 && (
              <div className="mt-3 p-3 rounded-2 bg-paper-2 text-mute text-[13px] border border-line">
                Nessun prezzo trovato.
                {searchProduct && ' Prova a cambiare la ricerca.'}
              </div>
            )}
          </div>

          <div className="mute text-[13px]">
            Dopo il click "Genera link", riceverai un link <strong>Stripe Checkout</strong> sicuro da mandare al cliente.
            Quando paga, l'abbonamento viene attivato automaticamente e appare in <strong>Servizi gestiti</strong> grazie al webhook.
          </div>
        </>
      )}

      {step === 'link' && link && (
        <div className="text-center py-6">
          <div className="w-14 h-14 mx-auto rounded-full grid place-items-center bg-ok-bg text-ok mb-4 border border-ok/30">
            <Icon name="check" size={24} />
          </div>
          <div className="t-h2 mb-2">Link Checkout pronto</div>
          <div className="mute mb-5">Copialo e mandalo al cliente, oppure aprilo subito per testare.</div>
          <div className="mx-auto" style={{ maxWidth: 540 }}>
            <div className="p-3 rounded-2 bg-paper-2 border border-line text-[13px] mono break-all text-left">
              {link}
            </div>
          </div>
          <Chip tone="info" className="mt-4">
            <Icon name="bell" size={10} /> Quando il cliente paga, l'abbonamento appare automaticamente via webhook
          </Chip>
        </div>
      )}
    </Modal>
  );
}
